import type { ReactNode } from 'react';
import { allCategories, YEAR_OPTIONS, DURATION_OPTIONS, type FilterState } from '@/lib/videos';

interface FilterPanelProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  showType?: boolean;
}

export default function FilterPanel({ filters, onChange, onReset, showType = true }: FilterPanelProps) {
  const update = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
          <i className="ri-filter-3-line text-primary-600" />
          筛选条件
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-primary-600"
        >
          <i className="ri-refresh-line" />
          重置
        </button>
      </div>

      {showType && (
        <FilterGroup title="类型">
          <Chip active={filters.type === 'all'} onClick={() => update({ type: 'all' })}>
            全部
          </Chip>
          {allCategories.map((category) => (
            <Chip
              key={category.key}
              active={filters.type === category.key}
              onClick={() => update({ type: category.key })}
            >
              {category.name}
            </Chip>
          ))}
        </FilterGroup>
      )}

      <FilterGroup title="年份">
        {YEAR_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            active={filters.year === option.value}
            onClick={() => update({ year: option.value })}
          >
            {option.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title="时长">
        {DURATION_OPTIONS.map((option) => (
          <Chip
            key={option.value}
            active={filters.duration === option.value}
            onClick={() => update({ duration: option.value })}
          >
            {option.label}
          </Chip>
        ))}
      </FilterGroup>
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
        {title}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
      }`}
    >
      {children}
    </button>
  );
}